import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { LoadContentDocument, ProcessContentDocument } from 'generated/resources/graphql'
import { isEqual } from 'lodash'
import { autorun } from 'mobx'

import { observer } from 'mobx-react'

import { useOne } from 'modules/ai/lib/useOne'
import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { LMSModuleAvailabilityWall } from 'modules/trainings/components/LMSModuleAvailabilityWall'
import ContentBuilder from 'modules/trainings/components/content/Builder'
import { contentRegistry } from 'modules/trainings/components/content/ContentRegistry'
import { ContentPreview } from 'modules/trainings/components/content/preview/ContentPreview'
import { ContentItem } from 'modules/trainings/lib/content/ContentItem'
import {
  contentFromAi,
  contentToAi,
  ModuleCollectionType,
} from 'modules/trainings/lib/content/aiTypes'
import { graphqlContentMapper } from 'modules/trainings/lib/content/mappers'

const ContentBuilderPage = observer(() => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { id: trainingId } = useParams()
  const session = useCurrentSession()
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isPreviewMode, setIsPreviewMode] = useState(false)

  // Load content modules for the current training.
  const { data, loading, refetch } = useQuery(LoadContentDocument, {
    variables: {
      trainingId: String(trainingId),
    },
  })

  // Save the content modules for the current training.
  const [processContent, { loading: isSaving }] = useMutation(ProcessContentDocument, {
    extractorFn(mutationData) {
      return mutationData.trainingsMutations.processContentTrainingsTraining
    },
  })

  // Convert the content modules from GraphQL to a ModuleCollection, which is used by the builder.
  const collection = useMemo(() => {
    const modules = data?.trainings.contentModules
    if (loading || !modules) {
      return undefined
    }
    return graphqlContentMapper.fromGraphQL(modules, String(trainingId))
  }, [trainingId, data, loading])

  // Initial state for the co-creation agent (used for both AI-enabled and AI-disabled modes)
  const initialAgentState = useMemo<ModuleCollectionType>(
    () => ({
      ...(collection ? contentToAi(collection) : { modules: [] }),
    }),
    [collection]
  )

  // useOne handles both AI-enabled and AI-disabled modes transparently
  const {
    coCreationData: agentState,
    setCoCreationData: setAgentState,
    isThinking,
  } = useOne<ModuleCollectionType>(['trainingContent'], initialAgentState, {
    autoOpenChat: true,
    initialMessage: i18n.t('trainings.one.initial_message'),
  })
  const agentStateRef = useRef(agentState)
  const bootstrapAgentStateRef = useRef<ModuleCollectionType | null>(null)

  useEffect(() => {
    agentStateRef.current = agentState
  }, [agentState])

  useEffect(() => {
    if (!collection || !agentState || bootstrapAgentStateRef.current) return

    const loadedState = contentToAi(collection)
    const hasLoadedContent = loadedState.modules.length > 0
    const hasSharedStateContent = agentState.modules.length > 0

    if (hasLoadedContent && !hasSharedStateContent) {
      bootstrapAgentStateRef.current = loadedState
      setAgentState({ ...loadedState })
    }
  }, [agentState, collection, setAgentState])

  // Sync from AI agent to local collection when agent updates content
  useEffect(() => {
    if (!agentState || !collection) return

    if (bootstrapAgentStateRef.current) {
      if (isEqual(agentState, bootstrapAgentStateRef.current)) {
        bootstrapAgentStateRef.current = null
      }
      return
    }

    if (isEqual(contentToAi(collection), agentState)) {
      // No changes from AI, no need to update collection or mark as unsaved
      return
    }

    contentFromAi(collection, agentState)
    setHasUnsavedChanges(true)
  }, [agentState, collection])

  // Sync from local collection to AI agent when collection changes
  useEffect(() => {
    return autorun(() => {
      if (setAgentState && collection) {
        const newState = contentToAi(collection)
        if (!isEqual(newState, agentStateRef.current)) {
          setAgentState({ ...newState })
        }
      }
    })
  }, [setAgentState, collection])

  if (!collection || loading) {
    return <Loading expand />
  }

  const handleSave = () => {
    if (!isSaving && !isThinking) {
      // Use GraphQL mapper to convert from domain to GraphQL input
      const contentModules = graphqlContentMapper.toGraphQL(collection)

      processContent({
        variables: {
          authorId: session.employee.id,
          companyId: session.company.id,
          trainingId: collection.trainingId,
          contentModules,
        },
      }).then((result) => {
        const handled = handleResult(result, () => {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('trainings.content.save.error'),
          })
        })

        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('trainings.content.save.success'),
          })
          setHasUnsavedChanges(false)
        }
      })
    }
  }

  const handleDiscard = () => {
    refetch()
    setHasUnsavedChanges(false)
  }

  return (
    <LMSModuleAvailabilityWall>
      {isPreviewMode && (
        <ContentPreview collection={collection} onClose={() => setIsPreviewMode(false)} />
      )}
      <ResponsivePage.Body
        variant="fullscreen"
        height="full"
        onActionClick={(action: string) => {
          if (action === 'preview') {
            setIsPreviewMode(true)
          } else if (action === 'back') {
            return navigate(
              navigation.trainings.list.details_revamp.view.overview.fullPath({
                id: String(trainingId),
              })
            )
          }
        }}
      >
        <ContentBuilder
          tocItems={collection.modules.map((contentModule) => ({
            id: contentModule.uuid,
            icon: contentRegistry.fetch(contentModule, 'icon'),
            label: contentModule.title,
            onClick: () => collection.setSelected(contentModule.uuid),
            children: contentModule.blocks.map((block) => ({
              id: block.uuid,
              label: block.title,
              icon: contentRegistry.fetch(block, 'icon'),
              onClick: () => collection.setSelected(block.uuid),
            })),
          }))}
          selectedItem={collection.selectedItem}
          trainingId={collection.trainingId}
          hasUnsavedChanges={hasUnsavedChanges}
          isThinking={isThinking}
          onReorder={(orderedStructure) => {
            collection.reorder(orderedStructure)
            setHasUnsavedChanges(true)
          }}
          onAddContent={({ contentItem }: { contentItem: ContentItem }) => {
            collection.addItem(contentItem)
            setHasUnsavedChanges(true)
          }}
          onDeleteContent={(uuid: string) => {
            collection.deleteItem(uuid)
            setHasUnsavedChanges(true)
          }}
          onContentChange={() => {
            setHasUnsavedChanges(true)
          }}
          onSave={handleSave}
          onDiscard={handleDiscard}
        />
      </ResponsivePage.Body>
    </LMSModuleAvailabilityWall>
  )
})

export default ContentBuilderPage
